import React from 'react';
import type { CandidateEngagementDetails } from '@axiom/validation';
import { CandidateOpportunitiesConst } from '@axiom/const';
import {
  Button,
  Modal,
  Paragraph,
  ModalSection,
  ModalHeader,
  ModalFooter,
  Grid,
  GridRow,
  GridColumn,
  Form,
  FormGroup,
  Radio,
  ParagraphHeader,
  Gutter,
} from '@axiom/ui';

import { CandidateOpportunitiesApi } from '../../api/candidate-opportunities-api';
import { EngagementsViewOptOutSchema } from '../../schema/engagements-view-opt-out-schema';
import { CandidateEngagementsApi } from '../../api/candidate-engagements-api';

const optOutReasons = {
  [CandidateOpportunitiesConst.ReasonForNo.DontHaveTime]: {
    label: "I don't have the time right now.",
    triggerText:
      'If there has been a change in your availability that will have an impact beyond this one engagement, please make the appropriate changes in "Preferences" to reflect that.',
  },
  [CandidateOpportunitiesConst.ReasonForNo.InconvenientLocation]: {
    label: 'This location is not convenient for me.',
    triggerText:
      'If there has been a change in your current location that will have an impact beyond this one engagement, please make the appropriate changes in "Preferences" to reflect that.',
  },
  [CandidateOpportunitiesConst.ReasonForNo.WrongAreaOfExpertise]: {
    label: 'This is not in line with my expertise.',
    triggerText:
      "This opportunity was sent to your Portal because of it's alignment with your past experiences. If you don't believe that to be the case, please ensure that your prior experience in your Profile is up to date.",
  },
  [CandidateOpportunitiesConst.ReasonForNo.Other]: {
    label: "I'd like to explain over the phone.",
    triggerText:
      "We'll reach out so you can tell us more about the reason for your decision.",
  },
};

export type EngagementViewOptOutModalProps = {
  engagement: CandidateEngagementDetails;
  onAfterSubmit: () => void;
  onClose: () => void;
  name?: string;
};

export const EngagementsViewOptOutModal = ({
  engagement,
  onAfterSubmit,
  onClose,
  name = 'ENGAGEMENTS_VIEW_OPT_OUT_MODAL',
}: EngagementViewOptOutModalProps) => {
  return (
    <Form
      schema={EngagementsViewOptOutSchema}
      initialValues={{
        candidateReasonForNo: undefined,
      }}
      onSubmit={async ({ candidateReasonForNo }) => {
        await CandidateOpportunitiesApi.updateEngagementOptOut({
          candidateId: engagement.candidate.id,
          opportunityId: engagement.opportunity.id,
          body: {
            candidateStatus:
              CandidateOpportunitiesConst.CandidateStatuses.TalentOptOut,
            candidateReasonForNo,
          },
        });

        // Refresh cache
        await Promise.all([
          CandidateEngagementsApi.refreshEngagementsCount(
            engagement.candidate.id
          ),
          CandidateEngagementsApi.refreshEngagementsCurrent(
            engagement.candidate.id
          ),
          CandidateEngagementsApi.refreshEngagementsHistory(
            engagement.candidate.id
          ),
        ]);

        onAfterSubmit();
      }}
    >
      {({ values, fireSubmit }) => (
        <Modal name={name}>
          <ModalHeader
            onClose={() => {
              onClose();
            }}
            name="MODAL_HEADER"
          >
            Opt Out of Opportunity
          </ModalHeader>
          <ModalSection>
            <ParagraphHeader>Reason for Opt-Out</ParagraphHeader>
            <Gutter bottom="8px" />
            <FormGroup
              name="candidateReasonForNo"
              label="Select the reason for your decision. This helps us better match you with future opportunities."
            >
              <Gutter bottom="8px" />
              <Grid>
                <GridRow>
                  <GridColumn width={6}>
                    <Radio
                      name="candidateReasonForNo"
                      displayValue={
                        optOutReasons[
                          CandidateOpportunitiesConst.ReasonForNo.DontHaveTime
                        ].label
                      }
                      option={
                        CandidateOpportunitiesConst.ReasonForNo.DontHaveTime
                      }
                    />
                    <Gutter bottom="24px" />
                    <Radio
                      name="candidateReasonForNo"
                      displayValue={
                        optOutReasons[
                          CandidateOpportunitiesConst.ReasonForNo
                            .InconvenientLocation
                        ].label
                      }
                      option={
                        CandidateOpportunitiesConst.ReasonForNo
                          .InconvenientLocation
                      }
                    />
                  </GridColumn>
                  <GridColumn width={6}>
                    <Radio
                      name="candidateReasonForNo"
                      displayValue={
                        optOutReasons[
                          CandidateOpportunitiesConst.ReasonForNo
                            .WrongAreaOfExpertise
                        ].label
                      }
                      option={
                        CandidateOpportunitiesConst.ReasonForNo
                          .WrongAreaOfExpertise
                      }
                    />
                    <Gutter bottom="24px" />
                    <Radio
                      name="candidateReasonForNo"
                      displayValue={
                        optOutReasons[
                          CandidateOpportunitiesConst.ReasonForNo.Other
                        ].label
                      }
                      option={CandidateOpportunitiesConst.ReasonForNo.Other}
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </FormGroup>
            {values.candidateReasonForNo ? (
              <Gutter vertical="24px">
                <Paragraph name="trigger-text">
                  {
                    optOutReasons[
                      values.candidateReasonForNo as keyof typeof optOutReasons
                    ].triggerText
                  }
                </Paragraph>
              </Gutter>
            ) : null}
          </ModalSection>
          <ModalFooter>
            <Button
              variation="outline"
              onClick={() => {
                onClose();
              }}
              name="CANCEL"
            >
              Cancel
            </Button>
            <Button onClick={fireSubmit} name="SUBMIT">
              Submit
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
